import { Mic } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Big tactile push-to-talk button.
 * Handles press-and-hold via pointer events + spacebar accessibility.
 */
export default function PTTButton({
  label = "Push to Talk",
  color = "blue",
  status = "idle", // idle | transmitting | receiving
  onPressStart,
  onPressEnd,
  disabled = false,
  size = "md",
  testId,
}) {
  const palette = {
    blue: { bg: "bg-blue-600", ring: "text-blue-600", text: "text-white" },
    green: { bg: "bg-emerald-600", ring: "text-emerald-600", text: "text-white" },
    orange: { bg: "bg-orange-600", ring: "text-orange-600", text: "text-white" },
    teal: { bg: "bg-teal-600", ring: "text-teal-600", text: "text-white" },
    amber: { bg: "bg-yellow-600", ring: "text-yellow-600", text: "text-white" },
    pink: { bg: "bg-pink-600", ring: "text-pink-600", text: "text-white" },
    slate: { bg: "bg-slate-900", ring: "text-slate-900", text: "text-white" },
    violet: { bg: "bg-violet-600", ring: "text-violet-600", text: "text-white" },
  }[color] || { bg: "bg-blue-600", ring: "text-blue-600", text: "text-white" };

  const sizeClasses = {
    xs: "h-9 w-9",
    sm: "h-14 w-14",
    md: "h-24 w-24",
    lg: "h-32 w-32",
  }[size];

  const iconSize = {
    xs: "h-4 w-4",
    sm: "h-5 w-5",
    md: "h-9 w-9",
    lg: "h-12 w-12",
  }[size];

  const handleDown = (e) => {
    e.preventDefault();
    if (disabled) return;
    onPressStart?.();
  };
  const handleUp = (e) => {
    e.preventDefault();
    if (disabled) return;
    onPressEnd?.();
  };

  const active = status === "transmitting";
  const receiving = status === "receiving";

  return (
    <button
      data-testid={testId}
      aria-label={label}
      disabled={disabled}
      onPointerDown={handleDown}
      onPointerUp={handleUp}
      onPointerLeave={(e) => { if (active) handleUp(e); }}
      onPointerCancel={handleUp}
      className={cn(
        "ptt-btn rounded-full grid place-items-center select-none outline-none",
        "focus-visible:ring-4 focus-visible:ring-blue-200",
        sizeClasses,
        active ? "is-active is-transmitting bg-emerald-600 text-white" :
          receiving ? "is-receiving bg-white text-blue-600 border-2 border-blue-500" :
          `${palette.bg} ${palette.text}`,
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <Mic className={iconSize} />
    </button>
  );
}
